import { EntityResolved } from '@api/api/entities';
import { RowSelectionState } from '@tanstack/react-table';
import { useOnboardingStore } from '@terminal/views/onboarding/hooks/useOnboardingStore';

export const useEntityChangeHandlers = (
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>
) => {
  const {
    setEntitiesList,
    setUnMappedEntitiesList,
    addManualEntityTags,
    removeManualEntityTag,
    removeMappedTag,
    appendMappedTags,
  } = useOnboardingStore(state => ({
    setEntitiesList: state.setEntitiesList,
    setUnMappedEntitiesList: state.setUnMappedEntitiesList,
    addManualEntityTags: state.addManualEntityTags,
    removeManualEntityTag: state.removeManualEntityTag,
    removeMappedTag: state.removeMappedTag,
    appendMappedTags: state.appendMappedTags,
  }));

  const onChangeEntity = (
    prevEntity: EntityResolved,
    newEntity: EntityResolved
  ) => {
    setRowSelection(prev => {
      const { [prevEntity.tag]: _removed, ...rest } = prev;
      return { ...rest, [newEntity.tag]: true };
    });

    removeManualEntityTag(prevEntity.tag);
    removeMappedTag(prevEntity.tag);
    addManualEntityTags([newEntity.tag]);
    appendMappedTags(
      [newEntity.tag],
      newEntity.input ? { [newEntity.tag]: newEntity.input } : undefined
    );

    const updatedList = useOnboardingStore
      .getState()
      .entitiesList.map(e => (e.tag === prevEntity.tag ? newEntity : e));
    const uniqByTag = new Map<string, EntityResolved>();
    updatedList.forEach(e => uniqByTag.set(e.tag, e));
    setEntitiesList(Array.from(uniqByTag.values()));
  };

  const onAddEntity = (entity: EntityResolved) => {
    setRowSelection(prev => ({ ...prev, [entity.tag]: true }));
    addManualEntityTags([entity.tag]);
    setEntitiesList([...useOnboardingStore.getState().entitiesList, entity]);
    setUnMappedEntitiesList([
      ...useOnboardingStore
        .getState()
        .unMappedEntitiesList.filter(e => e.input !== entity.input),
    ]);
  };

  const onRemoveEntity = (entity: EntityResolved) => {
    setRowSelection(prev => {
      const { [entity.tag]: _removed, ...rest } = prev;
      return rest;
    });
    removeManualEntityTag(entity.tag);
    removeMappedTag(entity.tag);
    setEntitiesList(
      useOnboardingStore
        .getState()
        .entitiesList.filter(e => e.tag !== entity.tag)
    );
    if (entity.input) {
      setUnMappedEntitiesList([
        ...useOnboardingStore.getState().unMappedEntitiesList,
        { input: entity.input },
      ]);
    }
  };

  return {
    onChangeEntity,
    onAddEntity,
    onRemoveEntity,
  };
};
